import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import type { CareMattersAnnuityDeltas } from '@shared/calculations/funding/projection/strategies/careMattersAnnuityStrategy';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { HeaderTooltip } from '../HeaderTooltip';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import type { StrategyPresentation } from './types';

// Presentation for Nationwide CareMatters Annuity (2943 / 2944).
//
// The generic columns would be misleading on three counts, which is why this
// exists rather than reusing forecarePresentation:
//
//  1. The death-benefit column is the CONTRACT VALUE. It is not a life face
//     amount and not a guaranteed floor — it is the account the LTC benefit is
//     accelerated out of, so it falls dollar for dollar as the acceleration
//     layer pays and can also GROW mid-claim on guaranteed interest.
//  2. The monthly benefit is derived from that contract value rather than fixed
//     at issue, so it moves every year even with no inflation rider.
//  3. When the rider IS elected it is a floor on the cap, not growth applied to
//     the pool, so the generic "pool x (1+r)^n" tooltip would explain the right
//     number with the wrong arithmetic.
//
// Page citations are the printed label on the carrier illustration; see
// @shared/calculations/docs/nationwide-carematters-annuity/README.md.

const pct = (rate: number | null | undefined, digits = 2) =>
  rate != null ? `${(rate * 100).toFixed(digits)}%` : null;

function accelerationMonthsOf(terms: ClaimTerms): number {
  return terms.baseBenefitPoolDurationMonths ?? 24;
}

/**
 * The inflation-rider floor for a policy year, as BOTH the symbolic form and the
 * evaluated numbers.
 *
 * This used to return only the symbolic string — "day-one monthly x (1 + 5%)^25"
 * — with no day-one value and no result, which is unverifiable by hand: you
 * cannot tell whether the floor or the contract value is the binding leg.
 *
 * The step count is `policyYear - 1` capped at the rider's cap years, which is
 * exactly what `growthFactor`/`compoundFactor` in sharedPolicyConfig use, so the
 * arithmetic shown here reproduces the engine's.
 */
function inflationFloor(
  terms: ClaimTerms,
  policyYear: number,
  dayOneMonthly: number,
): { steps: number; factor: number; value: number; expression: string } | null {
  const rate = terms.inflationRate ?? 0;
  if (rate <= 0) return null;
  const rawSteps = Math.max(Math.floor(policyYear) - 1, 0);
  const capYears = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
  const steps = capYears != null ? Math.min(rawSteps, capYears) : rawSteps;
  const isSimple = terms.inflationIsSimple === true;
  const factor = isSimple ? 1 + rate * steps : Math.pow(1 + rate, steps);
  const rateLabel = pct(rate) ?? 'rider rate';
  return {
    steps,
    factor,
    value: dayOneMonthly * factor,
    expression: isSimple
      ? `(1 + ${rateLabel} x ${steps})`
      : `(1 + ${rateLabel})^${steps}`,
  };
}

/** The day-one monthly cap: the contract value at issue over the acceleration window. */
function dayOneMonthlyOf(terms: ClaimTerms): number {
  const months = accelerationMonthsOf(terms);
  const atIssue = terms.accumulatedValueAtIssue ?? 0;
  return months > 0 ? atIssue / months : 0;
}

/** One `label / value` pair in a derivation grid. */
function DerivationRow({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <>
      <span className={muted ? 'text-gray-400' : 'text-gray-300'}>{label}</span>
      <span
        className={`tabular-nums ${strong ? 'font-semibold' : ''} ${muted ? 'text-gray-400' : ''}`}
      >
        {value}
      </span>
    </>
  );
}

function MonthlyBenefitTooltip({
  terms,
  policyYear,
  contractValue,
  cap,
}: {
  terms: ClaimTerms;
  policyYear: number;
  contractValue: number;
  cap: number;
}) {
  const months = accelerationMonthsOf(terms);
  const fromContractValue = months > 0 ? contractValue / months : 0;
  const dayOne = dayOneMonthlyOf(terms);
  const floor = inflationFloor(terms, policyYear, dayOne);
  // Compare the two LEGS against each other, never the cap against a leg. The
  // old check was `cap > contractValue/months`, fed the acceleration pool as
  // "contract value" — and pool ÷ months IS the cap, so it reported the
  // contract value as binding in every year the floor actually won.
  const floorWins = floor != null && floor.value > fromContractValue + 0.005;

  return (
    <TooltipContent className="max-w-sm space-y-1.5 text-xs">
      <div className="font-medium">Maximum monthly benefit · policy year {Math.floor(policyYear)}</div>
      <div className="text-[10px] text-gray-300">
        The contract value spread across the {months}-month acceleration window, floored
        by the inflation rider when one is elected. The policy pays whichever is greater.
      </div>

      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-mono text-[11px]">
        <DerivationRow label="Contract value" value={currency(contractValue)} />
        <DerivationRow label={`÷ ${months} months`} value={currency(fromContractValue)} muted={floorWins} />
        {floor && (
          <>
            <DerivationRow label="Day-one monthly" value={currency(dayOne)} />
            <DerivationRow
              label={`× ${floor.expression}`}
              value={currency(floor.value)}
              muted={!floorWins}
            />
          </>
        )}
        <DerivationRow label={floor ? 'Greater of the two' : 'Monthly cap'} value={currency(cap)} strong />
      </div>

      {floor && (
        <div className={floorWins ? 'text-emerald-700' : 'text-muted-foreground'}>
          {floorWins
            ? 'The inflation floor is the binding one this year.'
            : 'The contract value is the binding one this year.'}
        </div>
      )}
    </TooltipContent>
  );
}

function ContractValueTooltip({
  terms,
  policyYear,
  value,
  prevValue,
}: {
  terms: ClaimTerms;
  policyYear: number;
  value: number;
  prevValue: number | null;
}) {
  const annualRate = terms.accumulatedValueGuaranteedInitialRate ?? 0;
  const monthlyChargeRate = terms.monthlyContinuationBenefitChargeRate ?? 0;
  const atIssue = terms.accumulatedValueAtIssue ?? 0;
  const growth = prevValue != null ? value - prevValue : value - atIssue;

  return (
    <TooltipContent className="max-w-sm space-y-1.5 text-xs">
      <div className="font-medium">Contract value · policy year {Math.floor(policyYear)}</div>
      <div className="text-[10px] text-gray-300">
        Compounds monthly: the rider charge comes off first, then interest is credited on
        what is left. Twelve of these steps make a policy year.
      </div>

      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-mono text-[11px]">
        <DerivationRow label="At issue" value={currency(atIssue)} muted />
        {prevValue != null && (
          <DerivationRow label="Prior year end" value={currency(prevValue)} />
        )}
        <DerivationRow label="Guaranteed rate" value={pct(annualRate) ?? '—'} muted />
        <DerivationRow
          label="Monthly rider charge"
          value={pct(monthlyChargeRate, 4) ?? '—'}
          muted
        />
        <DerivationRow
          label={prevValue != null ? 'Growth this year' : 'Growth to date'}
          value={currency(growth)}
        />
        <DerivationRow label="Year end" value={currency(value)} strong />
      </div>

      <div className="text-[10px] text-gray-400">
        Monthly step: (value − value × {pct(monthlyChargeRate, 4) ?? 'charge'}) × (1 +{' '}
        {pct(annualRate) ?? 'rate'} ÷ 12 effective)
      </div>
    </TooltipContent>
  );
}

function TotalLtcTooltip({
  terms,
  policyYear,
  cap,
  total,
}: {
  terms: ClaimTerms;
  policyYear: number;
  cap: number;
  total: number;
}) {
  const accelerationMonths = accelerationMonthsOf(terms);
  const benefitPeriodMonths = terms.baseBenefitPoolDurationMonths
    ? Math.round(total / Math.max(cap, 1))
    : accelerationMonths;
  const extensionMonths = Math.max(benefitPeriodMonths - accelerationMonths, 0);

  return (
    <TooltipContent className="max-w-sm space-y-1.5 text-xs">
      <div className="font-medium">Total LTC dollars · policy year {Math.floor(policyYear)}</div>
      <div className="text-[10px] text-gray-300">
        Everything the policy could pay for care if a claim opened this year and ran to
        exhaustion. Both layers pay at the same monthly cap; only the durations differ.
      </div>

      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-mono text-[11px]">
        <DerivationRow label="Monthly cap" value={currency(cap)} />
        <DerivationRow
          label={`Acceleration × ${accelerationMonths}`}
          value={currency(cap * accelerationMonths)}
        />
        {extensionMonths > 0 && (
          <DerivationRow
            label={`Extension × ${extensionMonths}`}
            value={currency(cap * extensionMonths)}
          />
        )}
        <DerivationRow
          label={`Total × ${benefitPeriodMonths} months`}
          value={currency(total)}
          strong
        />
      </div>
    </TooltipContent>
  );
}

/**
 * Pool limit for CareMatters Annuity.
 *
 * The generic PoolLimitCell explains the number as `at-issue pool x inflation
 * factor`, which is the wrong math here: this product's pools are
 * `monthly cap x months`, and the cap is itself
 * `max(contract value / acceleration months, day-one monthly x inflation)`.
 * At-issue is where the contract value STARTED, not a term in the pool formula
 * — which is what made the generic tooltip unfollowable on this product.
 *
 * It stays visible, with the chain that connects it to the current figure
 * spelled out, so the derivation can be checked end to end by hand.
 */
// Exported for tests: `poolLimitCell` returns an unrendered element, so a test
// walking that tree sees only the component reference. Calling this directly
// yields the JSX to assert on, the same way payoutCellTooltip is tested.
export function CareMattersAnnuityPoolLimitCell({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot | undefined;
  terms: ClaimTerms;
}) {
  if (!snapshot) return <span className="text-gray-300">—</span>;

  const accelerationMonths = accelerationMonthsOf(terms);
  const total = snapshot.theoreticalLtcPoolCeiling;
  const cap = snapshot.monthlyBenefitCap;
  const contractValue = snapshot.contractValue ?? snapshot.effectiveFaceAmount;
  const atIssue = terms.accumulatedValueAtIssue ?? 0;
  const benefitPeriodMonths = cap > 0 ? Math.round(total / cap) : accelerationMonths;
  const extensionMonths = Math.max(benefitPeriodMonths - accelerationMonths, 0);

  const dayOne = dayOneMonthlyOf(terms);
  const floor = inflationFloor(terms, snapshot.policyYear, dayOne);
  const fromContractValue = accelerationMonths > 0 ? contractValue / accelerationMonths : 0;
  const floorWins = floor != null && floor.value > fromContractValue + 0.005;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help leading-tight">
          <div className="font-medium text-gray-900">{currency(total)}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm space-y-1.5 text-xs">
        <div className="font-medium">
          Pool limit · policy year {Math.floor(snapshot.policyYear)}
        </div>
        <div className="text-[10px] text-gray-300">
          Everything the policy could pay for care if a claim opened this year. Both
          layers pay at the same monthly cap; only the durations differ.
        </div>

        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-mono text-[11px]">
          <DerivationRow label="Contract value at issue" value={currency(atIssue)} muted />
          <DerivationRow label="…grown to this year" value={currency(contractValue)} />
          <DerivationRow label={`÷ ${accelerationMonths} months`} value={currency(fromContractValue)} muted={floorWins} />
          {floor && (
            <DerivationRow
              label={`floor: ${currency(dayOne)} × ${floor.expression}`}
              value={currency(floor.value)}
              muted={!floorWins}
            />
          )}
          <DerivationRow label="Monthly cap" value={currency(cap)} strong />
          <DerivationRow
            label={`Acceleration × ${accelerationMonths}`}
            value={currency(cap * accelerationMonths)}
          />
          {extensionMonths > 0 && (
            <DerivationRow
              label={`Extension × ${extensionMonths}`}
              value={currency(cap * extensionMonths)}
            />
          )}
          <DerivationRow label="Pool limit" value={currency(total)} strong />
        </div>

        <div className="text-[10px] text-gray-400">
          At issue is the contract value&rsquo;s starting point, not a term in the pool
          formula — the pool is the monthly cap multiplied out.
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function buildAccumulationColumns({ terms, timeline }: AccumulationCtx): AccumulationColumnSpec[] {
  const months = accelerationMonthsOf(terms);
  const rate = pct(terms.accumulatedValueGuaranteedInitialRate) ?? 'the guaranteed rate';
  const chargeRate = pct(terms.monthlyContinuationBenefitChargeRate, 4);

  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  // `contractValue` is the real account value; `effectiveFaceAmount` is the
  // ACCELERATION POOL (cap x acceleration months), which only equals it while
  // the inflation floor is not binding. Rendering the face amount under a
  // "Contract value" header overstated it by 75% at policy year 26 on a 5%
  // rider (338,635 against 193,094) and made the cap derivation circular,
  // because pool / months is the cap by construction.
  const contractValueOf = (row: { snapshot: YearSnapshot }) =>
    row.snapshot.contractValue ?? row.snapshot.effectiveFaceAmount;

  if (timeline.some(r => contractValueOf(r) > 0.5)) {
    columns.push({
      key: 'contract-value',
      label: 'Contract value',
      tooltip: (
        <HeaderTooltip
          description="The annuity account the LTC benefit is accelerated out of. It is also the death benefit — anything long-term care does not consume is paid to beneficiaries."
          formulas={[
            {
              label: 'Monthly step',
              expression: '(prior value - rider charge) × (1 + guaranteed monthly rate)',
            },
          ]}
          notes={
            chargeRate
              ? `Guaranteed ${rate} a year, less a ${chargeRate} monthly rider charge.`
              : `Guaranteed ${rate} a year.`
          }
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={contractValueOf(row)}
          prevValue={prev ? contractValueOf(prev) : null}
          tooltip={
            <ContractValueTooltip
              terms={terms}
              policyYear={row.snapshot.policyYear}
              value={contractValueOf(row)}
              prevValue={prev ? contractValueOf(prev) : null}
            />
          }
        />
      ),
    });
  }

  columns.push({
    key: 'monthly-benefit',
    label: 'Monthly benefit',
    tooltip: (
      <HeaderTooltip
        description={`The most this policy pays in a month if care starts that year. It is the contract value spread over the ${months}-month acceleration window, floored by the inflation rider when one is elected.`}
        formulas={[
          {
            label: 'Monthly cap',
            expression: `max(contract value ÷ ${months}, day-one monthly × inflation factor)`,
          },
        ]}
      />
    ),
    render: (row, prev) => (
      <GrowthValueCell
        value={row.snapshot.monthlyBenefitCap}
        prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
        tooltip={
          <MonthlyBenefitTooltip
            terms={terms}
            policyYear={row.snapshot.policyYear}
            contractValue={contractValueOf(row)}
            cap={row.snapshot.monthlyBenefitCap}
          />
        }
      />
    ),
  });

  if (timeline.some(r => r.snapshot.theoreticalLtcPoolCeiling > 0.5)) {
    columns.push({
      key: 'ltc-pool',
      label: 'Total LTC dollars',
      tooltip: (
        <HeaderTooltip
          description="Everything the policy could pay for care if a claim started that year and ran to exhaustion — the acceleration layer plus the extension layer."
          formulas={[{ label: 'Total', expression: 'monthly cap × total benefit period' }]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.theoreticalLtcPoolCeiling}
          prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
          tooltip={
            <TotalLtcTooltip
              terms={terms}
              policyYear={row.snapshot.policyYear}
              cap={row.snapshot.monthlyBenefitCap}
              total={row.snapshot.theoreticalLtcPoolCeiling}
            />
          }
        />
      ),
    });
  }

  return columns;
}

export const careMattersAnnuityPresentation: StrategyPresentation = {
  accumulationColumns: buildAccumulationColumns,

  // The death-benefit slot holds the contract value, so name it what the
  // carrier's own ledger names it.
  columnLabels: {
    'death-benefit': 'Contract value',
  },

  poolLimitCell: ({ snapshot, terms }) => (
    <CareMattersAnnuityPoolLimitCell snapshot={snapshot} terms={terms} />
  ),

  poolCellTooltip: ({ month, openState, terms }) => {
    const deltas = month.deltas.strategyDeltas as CareMattersAnnuityDeltas | undefined;
    const acceleration = openState.pools.baseBenefitPool;
    const extension = openState.pools.extensionOfBenefitsPool;
    if (getLtcPool(month.state) <= 0 && acceleration + extension <= 0) return null;

    const accelerationMonths = accelerationMonthsOf(terms);
    const extensionMonths = terms.extensionOfBenefitsPoolDurationMonths ?? 0;

    return (
      <BalanceBreakdownTooltip
        title="LTC dollars remaining"
        description="Care is paid from the acceleration layer first, and only then from the extension layer."
        sections={[
          {
            heading: `Acceleration (${accelerationMonths} months)`,
            opening: acceleration,
            closing: month.state.pools.baseBenefitPool,
            // The retroactive lump is drawn from these same pools, so it has to
            // appear here or the arithmetic will not reach the closing balance
            // on the reimbursement row.
            lines: [
              {
                label: 'Benefit paid',
                value: deltas?.acceleratedBenefitDraw ?? 0,
                kind: 'subtract',
              },
              {
                label: 'Waiting-period reimbursement',
                value: deltas?.epLumpSumBasePaid ?? 0,
                kind: 'subtract',
              },
            ],
            note: 'This layer IS the contract value — every dollar paid here also comes off the death benefit.',
          },
          {
            heading: `Extension of benefits (${extensionMonths} months)`,
            opening: extension,
            closing: month.state.pools.extensionOfBenefitsPool,
            lines: [
              {
                label: 'Inflation increase',
                value: month.deltas.eob.inflationAccrual,
                kind: 'add',
              },
              {
                label: 'Benefit paid',
                value: deltas?.extendedBenefitDraw ?? 0,
                kind: 'subtract',
              },
              {
                label: 'Waiting-period reimbursement',
                value: deltas?.epLumpSumEobPaid ?? 0,
                kind: 'subtract',
              },
            ],
            note: 'Pays once the contract value is exhausted, so it leaves nothing for beneficiaries.',
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const deltas = month.deltas.strategyDeltas as CareMattersAnnuityDeltas | undefined;
    const opening = openState.pools.deathBenefit;
    const closing = month.state.pools.deathBenefit;
    if (opening <= 0 && closing <= 0) return null;

    const drawn = deltas?.acceleratedBenefitDraw ?? 0;
    // ONLY the acceleration half of the retroactive lump reduces the contract
    // value. The extension half is paid from a pool that has no contract value
    // behind it, so subtracting the whole lump here overstated the reduction and
    // disagreed with the closing balance.
    const epDrawn = deltas?.epLumpSumBasePaid ?? 0;
    const interest = deltas?.contractValueInterestCredit ?? 0;
    const charge = deltas?.contractValueRiderCharge ?? 0;

    return (
      <TooltipContent className="max-w-sm space-y-1.5 text-xs">
        <div className="font-medium">Contract value / death benefit</div>
        <div>
          One balance serving both purposes. Long-term care draws reduce it dollar for
          dollar; whatever survives the claim is paid to beneficiaries.
        </div>
        <div className="space-y-0.5 font-mono text-[11px]">
          <div>opening {currency(opening)}</div>
          {drawn > 0 && <div>− {currency(drawn)} LTC benefit paid</div>}
          {epDrawn > 0 && <div>− {currency(epDrawn)} waiting-period reimbursement</div>}
          {charge > 0 && <div>− {currency(charge)} rider charge</div>}
          {interest > 0 && <div>+ {currency(interest)} guaranteed interest</div>}
          <div className="pt-0.5 font-medium">closing {currency(closing)}</div>
        </div>
        {charge === 0 && drawn > 0 && (
          <div className="text-muted-foreground">
            The rider charge is waived in months a benefit is paid.
          </div>
        )}
        {closing <= 0 && (
          <div className="text-amber-700">
            Exhausted — care continues to be paid from the extension layer, but there is
            nothing left for beneficiaries.
          </div>
        )}
      </TooltipContent>
    );
  },

  payoutCellTooltip: ({ month }) => {
    // `indemnityPayoutWaterfall` returns BARE grid rows, not a TooltipContent.
    // Returned unwrapped they are not a Radix popover at all — cells.tsx drops
    // them in as a sibling of the TooltipTrigger, so every row rendered the
    // whole waterfall INLINE and the payout column grew to fit it. Every other
    // strategy wraps it; this one did not.
    //
    // The rows are a two-column grid (`col-span-2` separators), so they need the
    // grid parent as well as the width cap.
    const epLumpSumPaid =
      (month.deltas.strategyDeltas as CareMattersAnnuityDeltas | undefined)?.epLumpSumPaid ?? 0;

    // Nothing to explain on a fully ineligible month unless room & board is
    // still out of pocket, or a retroactive lump landed here. PaidCell already
    // shows the reason natively. Mirrors genericHybridPresentation.
    if (
      month.facts.coverageEligibleDays === 0 &&
      (month.facts.roomAndBoardCost ?? 0) === 0 &&
      epLumpSumPaid === 0
    ) {
      return null;
    }

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — cash indemnity</div>
        <div className="mb-1 text-[10px] text-gray-300">
          The full monthly benefit is paid regardless of what care actually cost, and may
          be spent on anything, including informal care.
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {indemnityPayoutWaterfall(month)}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const deltas = month.deltas.strategyDeltas as CareMattersAnnuityDeltas | undefined;
    const surplusApplied = deltas?.indemnitySurplusApplied ?? 0;
    const epLump = deltas?.epLumpSumPaid ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (surplusApplied === 0 && epLump === 0) return null;

    return (
      <>
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {epLump > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block cursor-help border-b border-dotted border-amber-300 text-[10px] text-amber-700">
                +{currency(epLump)} waiting-period reimbursement
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm space-y-1.5 text-xs">
              <div className="font-medium">Retroactive elimination period</div>
              <div>
                The waiting period is paid back once it has been served, so the first
                payment covers the withheld days as well as the current month.
              </div>
              <div className="font-mono text-[11px]">
                {(deltas?.epLumpSumDays ?? 0).toFixed(0)} days ×{' '}
                {currency(deltas?.epLumpSumDailyRate ?? 0)} = {currency(epLump)}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  },

  drawnCellLabels: {
    description:
      'Benefit dollars paid to date, split by the layer they came from. Acceleration dollars also reduce the death benefit; extension dollars do not, because by then there is none left.',
    baseLabel: 'Acceleration drawn',
    cobLabel: 'Extension of benefits drawn',
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="Two layers. Care is paid from the acceleration layer first — that is the contract value itself — and only then from the extension layer."
        formulas={[
          { label: 'Acceleration', expression: 'contract value' },
          { label: 'Extension', expression: 'monthly cap × extension months' },
        ]}
        notes="Both layers pay at the same monthly cap; only the durations differ by risk class."
      />
    ),

    payoutColumnTooltip: (
      <HeaderTooltip
        description="Cash indemnity. The policy pays the full monthly benefit whenever the claim is eligible, whatever care actually costs, and the money can be used for anything."
        formulas={[
          { label: 'Paid', expression: 'monthly cap × (eligible days ÷ 30)' },
          { label: 'Care cost covered', expression: 'this month’s cash + surplus reserve drawn' },
        ]}
        notes="This column is the CASH the policy pays, which is capped. In a month where care costs more than the cap, the reserve built up in cheaper months is drawn on as well — so the care cost covered can exceed the cash paid, and out-of-pocket stays at zero until the reserve runs out. Hover a row to see the split."
      />
    ),

    benefitAmountTooltip: (
      <HeaderTooltip
        description="The most the policy pays in one month. It is FIXED WHEN THE CLAIM OPENS — the contract value is being drawn down from that point, and the cap does not fall with it. The inflation rider, if elected, keeps raising a floor underneath, so the cap can rise but never drop during a claim."
        formulas={[
          {
            label: 'Fixed at claim start',
            expression: 'contract value when the claim opened ÷ acceleration months',
          },
          {
            label: 'Inflation floor',
            expression: 'day-one monthly × inflation factor (this policy year)',
          },
          { label: 'Monthly cap', expression: 'the greater of the two' },
          { label: 'Paid this month', expression: 'monthly cap × (eligible days ÷ 30)' },
        ]}
        notes="Before the claim opens the cap tracks the growing contract value; the accumulation table shows that. Once the claim opens it is frozen at the value it reached."
      />
    ),

    deathBenefitTooltip: (
      <HeaderTooltip
        description="The remaining contract value. This product has no separate life face amount and no guaranteed minimum — beneficiaries receive whatever long-term care did not use."
        formulas={[
          { label: 'Death benefit', expression: 'contract value after LTC draws and interest' },
        ]}
        notes="It reaches zero once the acceleration layer is spent, so a long claim leaves nothing behind."
      />
    ),

    costTooltip: (
      <HeaderTooltip
        description="Projected cost of care for the month."
        notes={PROJECTED_COST_NOTE}
      />
    ),

    drawnTooltip: (
      <HeaderTooltip
        description="Benefit dollars paid to date, split by the layer they came from."
        formulas={[{ label: 'Drawn', expression: 'acceleration paid + extension paid' }]}
      />
    ),
  },
};
